import { Input } from '@/components/atoms'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import type { FieldDef } from './institutionalFields'
import {
  BODY_TEXT,
  CHECKBOX_CLASS,
  CHECKBOX_TICK_DARK,
  ERROR_TEXT,
  FIELD_LABEL,
  HELP_TEXT,
} from './styles'

interface FormFieldProps {
  field: FieldDef
  value: string | boolean
  onChange: (value: string | boolean) => void
  error?: string
  /**
   * For a write-only field: the server already holds a value the browser can
   * never read back. Renders an "on file" note plus the empty box to re-enter.
   */
  onFile?: boolean
  disabled?: boolean
}

const textInputClasses = 'h-[52px] font-plus-jakarta text-[14px]'

/**
 * One field of the institutional profile, rendered from its `FieldDef`.
 *
 * Responsive by CSS (`span` maps to a grid column count), so there is no
 * Desktop/Mobile split — the markup genuinely does not differ between
 * breakpoints, only the column count does.
 */
export const FormField = ({
  field,
  value,
  onChange,
  error,
  onFile,
  disabled,
}: FormFieldProps) => {
  const describedBy = error ? `${field.name}-error` : field.help ? `${field.name}-help` : undefined

  if (field.kind === 'checkbox') {
    return (
      <label
        htmlFor={field.name}
        className={cn(
          'flex cursor-pointer items-start gap-3 py-1',
          field.span === 2 && 'sm:col-span-2',
        )}
      >
        <Checkbox
          id={field.name}
          checked={Boolean(value)}
          onCheckedChange={checked => onChange(checked === true)}
          disabled={disabled}
          className={cn(CHECKBOX_CLASS, CHECKBOX_TICK_DARK, error && 'border-[#ff540c]')}
        />
        <span className="min-w-0">
          <span className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {field.label}
          </span>
          {field.help && <span className={cn(HELP_TEXT, 'block mt-0.5')}>{field.help}</span>}
          {error && (
            <span id={`${field.name}-error`} className={cn(ERROR_TEXT, 'block')}>
              {error}
            </span>
          )}
        </span>
      </label>
    )
  }

  return (
    <div className={cn('min-w-0', field.span === 2 && 'sm:col-span-2')}>
      <label className={FIELD_LABEL} htmlFor={field.name}>
        {field.label}
      </label>

      {field.kind === 'select' ? (
        <Select
          value={typeof value === 'string' ? value : ''}
          onValueChange={next => onChange(next)}
          disabled={disabled}
        >
          <SelectTrigger
            id={field.name}
            className={cn(textInputClasses, error && 'border-[#ff540c]')}
            aria-describedby={describedBy}
          >
            <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.kind === 'textarea' ? (
        <textarea
          id={field.name}
          value={typeof value === 'string' ? value : ''}
          onChange={event => onChange(event.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          rows={3}
          aria-describedby={describedBy}
          className={cn(
            'flex w-full rounded-lg border border-border bg-background px-3 py-3',
            'font-plus-jakarta text-[14px] text-foreground placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-0 focus:border-border transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-[#ff540c]',
          )}
        />
      ) : (
        <Input
          id={field.name}
          type={
            field.kind === 'email'
              ? 'email'
              : field.kind === 'tel'
                ? 'tel'
                : field.kind === 'date'
                  ? 'date'
                  : 'text'
          }
          value={typeof value === 'string' ? value : ''}
          onChange={event => onChange(event.target.value)}
          placeholder={onFile ? 'Saved — enter a new value to replace it' : field.placeholder}
          disabled={disabled}
          autoComplete={field.writeOnly ? 'off' : undefined}
          aria-describedby={describedBy}
          className={cn(textInputClasses, error && 'border-[#ff540c]')}
        />
      )}

      {onFile && (
        <p className={cn(HELP_TEXT, 'mt-1')}>
          <span className="font-medium text-[#1fb845]">On file.</span> This value is encrypted and
          never sent back to your browser. Leave blank to keep it.
        </p>
      )}
      {/* The error REPLACES the help rather than stacking under it: both are
          12px/18px with the same `mt-1`, so the swap is the same box and the
          field does not grow. This also matches `describedBy` above, which
          already points at one or the other, never both. */}
      {field.help && !onFile && !error && (
        <p id={`${field.name}-help`} className={cn(HELP_TEXT, 'mt-1')}>
          {field.help}
        </p>
      )}
      {error && (
        <p id={`${field.name}-error`} className={ERROR_TEXT}>
          {error}
        </p>
      )}
    </div>
  )
}

/** A standalone checkbox that is not part of the profile field schema. */
export const CheckboxRow = ({
  id,
  checked,
  onChange,
  label,
  help,
  error,
  disabled,
}: {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  help?: string
  error?: string
  disabled?: boolean
}) => (
  <label htmlFor={id} className="flex cursor-pointer items-start gap-3 py-1">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={next => onChange(next === true)}
      disabled={disabled}
      className={cn(CHECKBOX_CLASS, CHECKBOX_TICK_DARK, error && 'border-[#ff540c]')}
    />
    <span className="min-w-0">
      <span className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {label}
      </span>
      {/* Same swap as FormField: the error takes the help's box, so a checkbox
          row keeps its height and the rows below it do not move. */}
      {help && !error && <span className={cn(HELP_TEXT, 'block mt-0.5')}>{help}</span>}
      {error && <span className={cn(ERROR_TEXT, 'block')}>{error}</span>}
    </span>
  </label>
)

/** Section heading + optional blurb, shared by every wizard step. */
export const SectionHeading = ({
  title,
  description,
}: {
  title: string
  description?: string
}) => (
  <div className="mb-4">
    <h3 className="font-plus-jakarta text-[16px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
      {title}
    </h3>
    {description && <p className={cn(BODY_TEXT, 'mt-1')}>{description}</p>}
  </div>
)
